import { memo } from 'react';
import { useRagnar } from '@mentimeter/ragnar-react';
import type { PrioritisationResultT } from '@mentimeter/visualizations';
import { Prioritisation } from '@mentimeter/visualizations';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import { SvgElement } from '../../shared/components/visualizations-legacy/SvgElement';
import { VisualizationUpdater } from '../../shared/components/visualizations-legacy/VisualizationUpdater';
import { useUniqueSelector } from '../../shared/hooks/useUniqueSelector';
import { ScreenReaderTable } from './ScreenReaderTable';

interface IndexedPrioritisationResult extends PrioritisationResultT {
  index: number;
}

interface Props {
  selector: string;
  data: IndexedPrioritisationResult[];
  animationMultiplier: 1 | 0;
  lineColor: string;
  fillColors: string[];
  textColor: string;
  isScoreHidden: boolean;
}

const PrioritisationComponent = memo<Props>(
  ({
    selector,
    fillColors,
    textColor,
    lineColor,
    animationMultiplier,
    data,
    isScoreHidden,
  }: Props) => {
    const prioritisation = new Prioritisation({
      selector: `#${selector}`,
      data,
      animationMultiplier,
      lineColor,
      textColor,
      fillColors,
      isScoreHidden,
    });

    return (
      <SvgElement aria-hidden="true">
        <VisualizationUpdater updateFn={prioritisation.update} />
        <g id={selector} />
      </SvgElement>
    );
  },
);

interface PrioritisationVisualizationProps {
  shouldHideResults: boolean;
  shouldAnimate: boolean;
  results: IndexedPrioritisationResult[];
}

export const LegacyPrioritisationVisualization = ({
  shouldAnimate,
  results,
  shouldHideResults,
}: PrioritisationVisualizationProps) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
    },
  } = useRagnar();
  const animationMultiplier = shouldAnimate ? 1 : 0;
  const selector = useUniqueSelector();

  return (
    <>
      <PrioritisationComponent
        selector={selector}
        fillColors={fillColors}
        textColor={textColor}
        lineColor={textColor}
        animationMultiplier={animationMultiplier}
        data={results}
        isScoreHidden={shouldHideResults}
      />
      <ScreenReaderOnly>
        <ScreenReaderTable
          results={results}
          shouldHideResults={shouldHideResults}
        />
      </ScreenReaderOnly>
    </>
  );
};
